import { Plane, MapPin, Car, DollarSign, Settings, Tag, Hotel, BookOpen, LogOut, Gauge, Layers, CreditCard, Mail, FileText, ImageIcon, Globe, Compass, Search, MessageSquareQuote } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

export type AdminSection =
  | "airports"
  | "zones"
  | "service_types"
  | "vehicles"
  | "km_rates"
  | "hotels"
  | "bookings"
  | "tour_bookings"
  | "promo_codes"
  | "payment_methods"
  | "email_settings"
  | "email_templates"
  | "hero_slides"
  | "landing_pages"
  | "destinations"
  | "tours"
  | "seo_pages"
  | "testimonials"
  | "settings";

const menuItems: { key: AdminSection; label: string; icon: typeof Plane }[] = [
  { key: "airports", label: "Aeropuertos", icon: Plane },
  { key: "zones", label: "Zonas", icon: MapPin },
  { key: "service_types", label: "Tipos de Servicio", icon: Layers },
  { key: "vehicles", label: "Vehículos", icon: Car },
  { key: "km_rates", label: "Tarifas KM", icon: Gauge },
  { key: "hotels", label: "Hoteles", icon: Hotel },
  { key: "bookings", label: "Reservas", icon: BookOpen },
  { key: "tour_bookings", label: "Reservas Tours", icon: Compass },
  { key: "promo_codes", label: "Promos", icon: Tag },
  { key: "payment_methods", label: "Payment Methods", icon: CreditCard },
  { key: "email_settings", label: "Correo", icon: Mail },
  { key: "email_templates", label: "Templates", icon: FileText },
  { key: "hero_slides", label: "Hero Slides", icon: ImageIcon },
  { key: "landing_pages", label: "Landing Pages", icon: Globe },
  { key: "destinations", label: "Destinos", icon: MapPin },
  { key: "tours", label: "Tours", icon: Compass },
  { key: "seo_pages", label: "SEO Pages", icon: Search },
  { key: "testimonials", label: "Testimonios", icon: MessageSquareQuote },
  { key: "settings", label: "Configuración", icon: Settings },
];

interface AdminSidebarProps {
  activeSection: AdminSection;
  onSectionChange: (section: AdminSection) => void;
}

export function AdminSidebar({ activeSection, onSectionChange }: AdminSidebarProps) {
  const { user, signOut } = useAuth();
  const { getSetting } = useSiteSettings();
  const navigate = useNavigate();
  const { state } = useSidebar();
  const siteTitle = getSetting("site_title", "Admin");
  const collapsed = state === "collapsed";

  const handleSignOut = () => {
    signOut();
    navigate("/admin");
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="p-4">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
            <Car className="h-4 w-4 text-primary-foreground" />
          </div>
          {!collapsed && (
            <div>
              <h2 className="text-sm font-bold">{siteTitle}</h2>
              <p className="text-[10px] text-muted-foreground">Admin Panel</p>
            </div>
          )}
        </div>
      </SidebarHeader>

      <Separator />

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Gestión</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.key}>
                  <SidebarMenuButton
                    onClick={() => onSectionChange(item.key)}
                    isActive={activeSection === item.key}
                    tooltip={item.label}
                  >
                    <item.icon className="h-4 w-4" />
                    {!collapsed && <span>{item.label}</span>}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-3">
        <Separator className="mb-3" />
        {!collapsed && (
          <p className="text-xs text-muted-foreground truncate mb-2 px-1">{user?.email}</p>
        )}
        <Button variant="ghost" size="sm" className="w-full justify-start gap-2" onClick={handleSignOut}>
          <LogOut className="h-4 w-4" />
          {!collapsed && <span>Cerrar Sesión</span>}
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
}

export { menuItems };
